import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { BehaviorSubject, catchError, filter, finalize, firstValueFrom, from, map, Observable, switchMap, take, throwError } from "rxjs";

import { REFRESH_BLACK_LIST_URL } from "@app/core/injections/refresh-black-list-url.injection";
import { AuthService } from "@app/core/services/auth.service";
import { SessionService } from "@app/core/services/session.service";
import { AccountService } from "@app/shared/services/account.service";

const ERROR_UNAUTHORIZED = 401;
const ERROR_UNAUTHORIZED_MESSAGE = "Unauthorized request";
const TOKEN_REFRESH_LOCK = "jwt-token-refresh";
const SESSION_EXPIRED_KEY = "AUTH.LOGIN.SESSION_EXPIRED";

@Injectable()
export class UnauthorizedInterceptor<T> implements HttpInterceptor {
  private readonly refresh$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly authService: AuthService,
    private readonly accountService: AccountService,
    private readonly sessionService: SessionService,
    @Inject(REFRESH_BLACK_LIST_URL)
    private readonly refreshBlackListUrl: (string | RegExp)[],
  ) {}

  intercept(req: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
    if (this.onSkipHandler(req)) {
      return next.handle(req);
    }

    const accessToken = this.authService.getAccessToken();

    if (accessToken) {
      req = this.addTokenToHeader(req, accessToken);
    }

    return next.handle(req).pipe(
      catchError(error => this.handlerHttpError(error, req, next))
    );
  }

  private addTokenToHeader(req: HttpRequest<T>, token: string): HttpRequest<T> {
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private onSkipHandler(req: HttpRequest<T>): boolean {
    return !!this.refreshBlackListUrl.find(url => req.url.match(url));
  }

  private handlerHttpError(error: HttpErrorResponse, req: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
    const isStatus401 = error.status === ERROR_UNAUTHORIZED;
    const isSkipUrl = this.onSkipHandler(req);

    if (isStatus401 && !isSkipUrl) {
      return this.onHandleUnauthorized(next, req);
    }

    return throwError(() => error);
  }

  private onHandleUnauthorized(next: HttpHandler, req: HttpRequest<T>): Observable<HttpEvent<T>> {
    if (this.refresh$.value) {
      return this.refresh$.pipe(
        filter(inProgress => !inProgress),
        take(1),
        switchMap(() => {
          const accessToken = this.authService.getAccessToken();

          if (!accessToken) {
            return throwError(() => ERROR_UNAUTHORIZED_MESSAGE);
          }

          return next.handle(this.addTokenToHeader(req, accessToken));
        }),
      );
    }

    this.refresh$.next(true);
    const staleAccessToken = this.authService.getAccessToken();

    const freshAccessToken$ = from(this.refreshUnderLock(staleAccessToken)).pipe(
      finalize(() => this.refresh$.next(false)),
    );

    return freshAccessToken$.pipe(
      catchError(() => {
        this.notifySessionEndedAndRedirect();

        return throwError(() => ERROR_UNAUTHORIZED_MESSAGE);
      }),
      switchMap(freshAccessToken => {
        if (!freshAccessToken) {
          this.notifySessionEndedAndRedirect();

          return throwError(() => ERROR_UNAUTHORIZED_MESSAGE);
        }

        return next.handle(this.addTokenToHeader(req, freshAccessToken));
      }),
    );
  }

  // Cross-tab single-flight: only one browser tab may hit /user/refresh at a time (Web Locks). If another
  // tab rotated the token while we waited for the lock, reuse its result instead of refreshing again — that
  // is what stops the "logged out in another tab" churn (a single-use refresh token being replayed twice).
  private refreshUnderLock(staleAccessToken: string | null): Promise<string | null> {
    const locks = navigator.locks;

    return locks?.request
      ? locks.request(TOKEN_REFRESH_LOCK, () => this.refreshAccessToken(staleAccessToken))
      : this.refreshAccessToken(staleAccessToken);
  }

  private async refreshAccessToken(staleAccessToken: string | null): Promise<string | null> {
    const currentAccessToken = this.authService.getAccessToken();
    if (currentAccessToken && currentAccessToken !== staleAccessToken) {
      // Another tab already refreshed while we waited for the lock — reuse its token, no second refresh.
      return currentAccessToken;
    }

    const refreshToken = this.authService.getRefreshToken();
    if (!refreshToken) {
      return null;
    }

    const { token, refreshToken: rotatedRefreshToken, refreshTokenExpires } =
      await firstValueFrom(this.accountService.refreshAuthToken(refreshToken).pipe(map(response => response.data)));

    this.authService.setAuthorizationData(token, rotatedRefreshToken, refreshTokenExpires);

    return token;
  }

  private notifySessionEndedAndRedirect(): void {
    this.sessionService.terminate(SESSION_EXPIRED_KEY);
  }
}
